import { HStack, VStack } from '@/components/Atoms'
import { type FilterSelectionMode } from '@/lib/utils/filters'
import { Checkbox, Label } from '@repo/ui/components'
import { LucideIcon } from 'lucide-react'
import { Container } from './Container'

export type CheckboxListValue<T extends FilterSelectionMode> = T extends 'single'
  ? string
  : Set<string>

type CheckboxListProps<T extends FilterSelectionMode = 'multiple'> = {
  selectionMode?: T
  name: string
  title: string
  options: { value: string; label: string; icon?: LucideIcon }[]
  selectedOptions: CheckboxListValue<T>
  setSelectedOptions: React.Dispatch<React.SetStateAction<CheckboxListValue<T>>>
  resetValue?: CheckboxListValue<T>
  /**
   * Called instead of writing the default value back, for a consumer whose setter cannot take an empty
   * write — a single-select panel that treats an emptying click as a no-op would otherwise swallow a
   * reset to no selection at all.
   */
  onReset?: () => void
}

export function CheckboxList<T extends FilterSelectionMode = 'multiple'>(
  props: CheckboxListProps<T>,
) {
  const defaultValue =
    props.resetValue ??
    ((props.selectionMode === 'single' ? '' : new Set<string>()) as CheckboxListValue<T>)

  const isChecked = (optionValue: string) =>
    typeof props.selectedOptions === 'string'
      ? props.selectedOptions === optionValue
      : props.selectedOptions.has(optionValue)

  const hasOptionSelected =
    typeof props.selectedOptions === 'string'
      ? Boolean(props.selectedOptions)
      : props.selectedOptions.size > 0
  const isDifferentFromDefaultValue = () => {
    if (typeof defaultValue === 'string') {
      return props.selectedOptions != defaultValue
    } else {
      const selectedOptions = props.selectedOptions as CheckboxListValue<'multiple'>
      return (
        selectedOptions.size != defaultValue.size ||
        selectedOptions.symmetricDifference(defaultValue).size !== 0
      )
    }
  }

  const handleReset = () => {
    if (props.onReset) return props.onReset()

    props.setSelectedOptions(defaultValue)
  }

  const handleCheckboxChange = (optionValue: string) => {
    props.setSelectedOptions((previous) => {
      if (props.selectionMode === 'single') {
        return (previous !== optionValue ? optionValue : '') as CheckboxListValue<T>
      } else {
        const next = new Set(previous)
        if (next.has(optionValue)) {
          next.delete(optionValue)
        } else {
          next.add(optionValue)
        }
        return next as CheckboxListValue<T>
      }
    })
  }

  return (
    <Container
      isResetVisible={props.resetValue ? isDifferentFromDefaultValue() : hasOptionSelected}
      onReset={handleReset}
      hasOptionsSelected={hasOptionSelected}
      title={props.title}
    >
      <VStack gap="sm">
        {props.options.map((option) => (
          <HStack gap="sm" key={option.value}>
            <Checkbox
              id={option.value}
              checked={isChecked(option.value)}
              onCheckedChange={() => handleCheckboxChange(option.value)}
            />
            <Label htmlFor={option.value}>{option.label}</Label>
          </HStack>
        ))}
      </VStack>
    </Container>
  )
}
